import { startCase } from 'lodash/fp';
import PropTypes from 'prop-types';
import * as React from 'react';
import { FieldMetaState, FieldProps, FieldRenderProps } from 'react-final-form';
import { getInputProps } from '../../../services/Validation';
import { FormField } from '../FormField';
import { FormInputWithWrapper } from '../FormInputs';
import { FormLabel } from '../FormLabel';
import styles from './style.m.less';

interface FromHeaderProps {
    children: React.ReactNode;
}

export const FormItemSeparator = () => <hr className={styles.hr} />;

export const FormHeader = ({ children }: FromHeaderProps) => (
    <>
        <FormItemSeparator />
        <h2 className={styles.header}>{children}</h2>
    </>
);

export function metaToErrorMessage<V>(meta: FieldMetaState<V>, showError: (meta: FieldMetaState<V>) => boolean) {
    if (meta && meta.error && showError(meta)) {
        return meta.error;
    }
    const showSubmitError = meta && meta.submitError && !meta.dirtySinceLastSubmit;
    if (showSubmitError) {
        return meta.submitError;
    }
    return undefined;
}

type FormItemProps<V, E extends HTMLElement> = FieldProps<V, FieldRenderProps<V, E>, E> & {
    showError?: (meta: FieldMetaState<V>) => boolean;
};

export function FormItem<V, E extends HTMLElement>({
    name,
    id,
    type = 'text',
    label,
    subLabel,
    info,
    inputComponent: InputComponent = FormInputWithWrapper,
    inputProps,
    validators,
    parse,
    format,
    allowNull,
    value,
    showError = meta => !!meta.touched,
}: FormItemProps<V, E>) {
    const prettyLabel = label !== undefined ? label : startCase(name);
    const { required } = getInputProps(validators);
    const isSelectionElement = ['checkbox', 'radio'].includes(type!);
    return (
        <FormField
            name={name}
            label={prettyLabel}
            validators={validators}
            parse={parse}
            format={format}
            value={value}
            type={type}
            allowNull={allowNull}
        >
            {({ input, meta }: FieldRenderProps<V, E>) => (
                <>
                    {!isSelectionElement && prettyLabel && (
                        <FormLabel htmlFor={id || name} subLabel={subLabel} info={info}>
                            {prettyLabel}
                        </FormLabel>
                    )}
                    <InputComponent
                        {...{
                            ...input,
                            name,
                            id: id || name,
                            label,
                            type,
                            required,
                            error: metaToErrorMessage(meta, showError),
                            ...inputProps,
                        }}
                    />
                </>
            )}
        </FormField>
    );
}

FormItem.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.node,
    subLabel: PropTypes.node,
    info: PropTypes.string,
    type: PropTypes.string,
    inputComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    inputProps: PropTypes.object,
    allowNull: PropTypes.bool,
    validators: PropTypes.arrayOf(
        PropTypes.shape({
            validate: PropTypes.func,
        })
    ),
};
